import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Input, Button, Card, Divider, Row, Col, notification, Select } from 'antd';
import { useLocation } from 'react-router-dom';
import { API_BASE } from './api/client';
import './EditJobForm.css';

const { TextArea } = Input;
const { Option } = Select;

const EditJobForm = ({ onJobCreated }) => {
    const location = useLocation();
    const jobId = location.state?.jobId; // Retrieve jobId from location state
    const [form] = Form.useForm();
    const [fields, setFields] = useState([]);
    const [formElements, setFormElements] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!jobId) {
            notification.error({ message: 'Job ID is missing!' });
            return;
        }
        fetchFormData(jobId); // Fetch form data using jobId
    }, [jobId]);

    const fetchFormData = async (jobId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/api/job/postjobs/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
    
            const { fields: fetchedFields, formElements: fetchedFormElements } = response.data;
    
            // Populate the fields and form elements
            setFields(fetchedFields || []);
            setFormElements(fetchedFormElements || []);
    
            // Prepare initial values for the form
            const initialValues = {};
                fetchedFields.forEach((field, index) => {
                initialValues[`fields[${index}].value`] = field.value || '';
            });
    
            // Update form values
            form.setFieldsValue(initialValues);
        } catch (error) {
            console.error('Error fetching form data:', error);
            notification.error({ message: 'Failed to fetch form data.' });
        }
    };    

    const validateFormElements = () => {
        let isValid = true;
    
        formElements.forEach((element, index) => {
            if (!element.name || element.name.trim() === '') {
                notification.error({
                    message: `Form Element "${element.label}" is missing a "name". Please correct it.`,
                });
                isValid = false;
            }
        });
    
        return isValid;
    };
    

    const handleFinish = async () => {
        if (!validateFormElements()) return;
        if (isSubmitting) return; // Prevent multiple submissions
        setIsSubmitting(true);
    
        try {
            const jobData = form.getFieldsValue(true); // Fetch form values
    
            // Map form data to fields payload
            const fieldsPayload = fields.map((field, index) => ({
                label: field.label,
                value: jobData[`fields[${index}].value`] || '', // Match dynamic field names
            }));
    
            // Map form elements
            const formElementsPayload = formElements.map((element) => ({
                label: element.label,
                name: element.name,
                type: element.type,
                ...(element.type === 'select' && { options: element.options }), // Include options for selects
            }));
    
            // Combine into a single payload
            const payload = {
                fields: fieldsPayload,
                formElements: formElementsPayload,
            };
    
            // API call
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${API_BASE}/api/job/postjobs/${jobId}`,
                payload,
                {headers: { Authorization: `Bearer ${token}` },});
    
                if (response.status >= 200 && response.status < 300) {
                    form.resetFields();
                    notification.success({
                        message: 'Job listing and application form submitted successfully!',
                    });
                } else {
                    throw new Error('Unexpected response status');
                }
            } catch (error) {
                const errorMessage =
                    error.response?.data?.message || // API provided error message
                    error.message || // JavaScript error message
                    'Failed to submit job and application form.'; // Fallback
        
                notification.error({ message: errorMessage });
            } finally {
                setIsSubmitting(false); // Reset the submitting state
            }
        };        

    const handleFieldChange = (index, key, value) => {
        const updatedFields = [...fields];
        updatedFields[index][key] = value;
        setFields(updatedFields);
    
        // Update form state
        const updatedValues = {};
        updatedFields.forEach((field, i) => {
            updatedValues[`fields[${i}].value`] = field.value;
        });
        form.setFieldsValue(updatedValues);
    };
    

    const addField = () => {
        setFields([
            ...fields,
            {
                label: 'New Field',
                value: '',
                type: 'text',
                required: false,
                name: `field_${fields.length + 1}`,
            },
        ]);
    };

    const removeField = (index) => {
        const updatedFields = fields.filter((_, i) => i !== index);
        setFields(updatedFields);
    };

    const generateName = (label) => {
        return label
            .toLowerCase() // Convert to lowercase
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .replace(/[^a-z0-9_]/g, ''); // Remove non-alphanumeric characters except underscores
    };    
    
    const handleFormElementChange = (index, key, value) => {
        const updatedFormElements = [...formElements];
    
        // If the key includes 'options', handle nested update
        if (key.startsWith('options[')) {
            const match = key.match(/options\[(\d+)\]\.(.+)/); // Extract option index and property
            if (match) {
                const optionIndex = parseInt(match[1], 10);
                const optionKey = match[2];
    
                // Update the specific option's property
                updatedFormElements[index].options[optionIndex][optionKey] = value;
            }
        } else {
            // Handle direct updates to the formElement
            updatedFormElements[index][key] = value;
    
            // Automatically generate 'name' when 'label' changes
            if (key === 'label') {
                updatedFormElements[index].name = generateName(value);
            }
        }
    
        setFormElements(updatedFormElements);
    };
        
    const handleAddOption = (index) => {
        const updatedFormElements = [...formElements];
        const newOption = { label: '', value: '' };
        updatedFormElements[index].options.push(newOption);
        setFormElements(updatedFormElements);
    };
    
    const handleRemoveOption = (index, optIndex) => {
        const updatedFormElements = [...formElements];
        updatedFormElements[index].options.splice(optIndex, 1);
        setFormElements(updatedFormElements);
    };    
    
    const addFormElement = () => {
        setFormElements([
            ...formElements,
            {
                label: 'New Form Element',
                type: 'text', // Default type
                options: [],
            },
        ]);
    }; 
    
    const removeFormElement = (index) => {
        const updatedFormElements = formElements.filter((_, i) => i !== index);
        setFormElements(updatedFormElements);
    };    

    const renderFormElementInput = (formElement, index) => (
        <Row key={index} gutter={16} style={{ marginBottom: 16 }}>
            <Col span={11}>
                <Input
                    placeholder="Label"
                    value={formElement.label}
                    onChange={(e) => handleFormElementChange(index, 'label', e.target.value)}
                />
            </Col>
            <Col span={11}>
                <Select
                    value={formElement.type}
                    onChange={(value) => handleFormElementChange(index, 'type', value)}
                    style={{ width: '100%' }}
                >
                    <Option value="text">Text</Option>
                    <Option value="textarea">Textarea</Option>
                    <Option value="email">Email</Option>
                    <Option value="number">Number</Option>
                    <Option value="date">Date</Option>
                    <Option value="file">File</Option>
                    <Option value="select">Select</Option>
                </Select>
            </Col>
            {formElement.type === 'select' && formElement.options && (
                <Col span={24}>
                    <Divider>Options for {formElement.label}</Divider>
                    {formElement.options.map((option, optIndex) => (
                        <Row key={optIndex} gutter={16} style={{ marginBottom: 8 }}>
                            <Col span={11}>
                            <Input
                                placeholder="Option Label"
                                value={option.label}
                                onChange={(e) =>
                                    handleFormElementChange(index, `options[${optIndex}].label`, e.target.value)
                                }
                            />
                            </Col>
                            <Col span={11}>
                            <Input
                                placeholder="Option Value"
                                value={option.value}
                                onChange={(e) =>
                                    handleFormElementChange(index, `options[${optIndex}].value`, e.target.value)
                                }
                            />
                            </Col>
                            <Col span={2}>
                                <Button
                                    type="text"
                                    danger
                                    icon={<i className="fas fa-trash"></i>}
                                    onClick={() => handleRemoveOption(index, optIndex)}
                                />
                            </Col>
                        </Row>
                    ))}
                    <Button
                        className='job-edit-add-btn'
                        type="dashed"
                        onClick={() => handleAddOption(index)}
                        block
                        icon={<i className="fas fa-plus" />}
                        style={{ marginBottom: 16 }}
                    >
                        Add Option
                    </Button>
                </Col>
            )}
            <Col span={2}>
                <Button
                    type="text"
                    danger
                    icon={<i className="fas fa-trash"></i>}
                    onClick={() => removeFormElement(index)}
                />
            </Col>
        </Row>
    );    

    return (
        <Card className="edit-job-form">
             <h2 className="admin-sub-header">Edit Job Form</h2>
            <Divider className="job-edit-divider" />
            <Form layout="vertical" onFinish={handleFinish} form={form}>
                {fields.map((field, index) => (
                    <Row className="job-edit-row" key={index} gutter={16} style={{ marginBottom: 16 }}>
                        <Col className="job-edit-col"  span={11}>
                            <Input
                                className="job-edit-input"
                                placeholder="Label"
                                value={field.label}
                                onChange={(e) =>
                                    handleFieldChange(index, 'label', e.target.value)
                                }
                            />
                        </Col>
                        <Col className="job-edit-col" span={11}>
                            <Input
                                className="job-edit-input"
                                name={`fields[${index}].value`} 
                                placeholder="Value"
                                value={field.value}
                                onChange={(e) =>
                                    handleFieldChange(index, 'value', e.target.value)
                                }
                            />
                        </Col>
                        <Col className="job-edit-col" span={2}>
                            <Button
                                className="job-edit-danger-btn"
                                type="text"
                                danger
                                icon={<i className="fas fa-trash"></i>}
                                onClick={() => removeField(index)}
                            />
                        </Col>
                    </Row>
                ))}
                <Button
                    className="job-edit-add-btn"
                    type="dashed"
                    onClick={addField}
                    block
                    icon={<i className="fas fa-plus" />}
                    style={{ marginBottom: 16 }}
                >
                    Add Field
                </Button>
            </Form>
            <Divider />
            <h2 className="admin-sub-header">Edit Job Application Form</h2>
            <Divider />
            <Form layout="vertical">
                {formElements.map((formElement, index) => renderFormElementInput(formElement, index))}
                <Button
                    className='job-edit-add-btn'
                    type="dashed"
                    onClick={addFormElement}
                    block
                    icon={<i className="fas fa-plus" />}
                    style={{ marginBottom: 16 }}
                >
                    Add Form Element
                </Button>
                <Form.Item style={{ textAlign: 'center' }}>
                    <Button onClick={handleFinish} className='admin-submit'><i className="fas fa-paper-plane"></i>
                        Submit
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default EditJobForm;
